import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFilter,
  faUserCircle,
  faCaretDown,
  faUser,
  faSignOutAlt,
  faCrown,
  faFileAlt,
  faChartLine,
  faHistory,
  faWeightHanging,
  faExclamationTriangle,
  faBolt,
  faTemperatureHigh,
  faPlus,
  faClock,
  faStar,
  faShareAlt,
  faCog,
  faQuestionCircle,
  faEye,
  faSyncAlt,
  faFilePdf,
  faFileExcel,
  faSpinner,
  faInfoCircle,
  faCheckCircle,
  faExclamationCircle,
  faChevronLeft,
  faChevronRight,
  faRedoAlt
} from '@fortawesome/free-solid-svg-icons';
import '../scss/ReportsExport.scss';
import BaseURL from '../assets/contants/BaseURL';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Reports = () => {
  const navigate = useNavigate();

  // State for filters
  const [filters, setFilters] = useState({
    reportType: 'operations',
    timeRange: 'today',
    craneSelection: 'all',
    reportFormat: 'pdf'
  });
  const [orgLogo, setOrgLogo] = useState(null);

  const getAuthToken = () => sessionStorage.getItem('Token') || localStorage.getItem('Token');

  useEffect(() => {
      const fetchOrgLogo = async () => {
        try {
          const token = getAuthToken();
          const organizationId = sessionStorage.getItem('organization_id');
  
          const response = await fetch(`${BaseURL}Userauth/organizations/${organizationId}/`, {
            headers: {
              'Authorization': `Token ${token}`,
              'Content-Type': 'application/json',
            }
          });
  
          if (response.ok) {
            const data = await response.json();
            if (data.data?.logo_url) { 
              setOrgLogo(data.data.logo_url);
            }
          }
        } catch (error) {
          console.error('Error fetching org logo:', error);
        }
      };
  
      fetchOrgLogo();
    }, []);

  // State for reports data
  const [reports, setReports] = useState([
    {
      id: 'RPT-20230615-001',
      type: 'Operations Log',
      category: 'Standard',
      dateRange: 'Jun 1-15, 2023',
      cranes: 'All Cranes',
      generatedOn: 'Jun 15, 2023 09:45',
      status: 'ready'
    },
    {
      id: 'RPT-20230614-002',
      type: 'Load Analysis',
      category: 'Custom',
      dateRange: 'Jun 1-14, 2023',
      cranes: 'CRN-001, CRN-002',
      generatedOn: 'Jun 14, 2023 14:30',
      status: 'ready'
    },
    {
      id: 'RPT-20230615-003',
      type: 'Error Summary',
      category: 'Scheduled',
      dateRange: 'Jun 10-15, 2023',
      cranes: 'CRN-003',
      generatedOn: 'Jun 15, 2023 11:20',
      status: 'processing'
    },
    {
      id: 'RPT-20230613-004',
      type: 'Voltage Analysis',
      category: 'Standard',
      dateRange: 'Jun 1-13, 2023',
      cranes: 'All Cranes',
      generatedOn: 'Jun 13, 2023 16:45',
      status: 'failed'
    },
    {
      id: 'RPT-20230612-005',
      type: 'Performance Trends',
      category: 'Custom',
      dateRange: 'May 1 - Jun 12, 2023',
      cranes: 'CRN-002, CRN-004',
      generatedOn: 'Jun 12, 2023 10:15',
      status: 'ready'
    }
  ]);

  // Stats data
  const stats = {
    reportsGenerated: 142,
    avgReportSize: '3.2MB',
    scheduledReports: 8,
    alertReports: 24
  }

  // UI States
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [notification, setNotification] = useState(null);

  // Chart ref
  const operationsChartRef = useRef();

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Chart data for operations
  const chartData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Hoisting Operations',
        data: [1200, 1900, 1500, 2000, 1800, 2100],
        backgroundColor: 'rgba(52, 152, 219, 0.7)',
        borderColor: 'rgba(52, 152, 219, 1)',
        borderWidth: 1
      },
      {
        label: 'Travel Operations',
        data: [800, 1200, 1000, 1500, 1300, 1600],
        backgroundColor: 'rgba(46, 204, 113, 0.7)',
        borderColor: 'rgba(46, 204, 113, 1)',
        borderWidth: 1
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: 'Number of Operations' }
      },
      x: {
        title: { display: true, text: 'Month' }
      }
    },
    plugins: {
      legend: { position: 'top' },
      tooltip: { mode: 'index', intersect: false }
    }
  };

  // Report cards data
  const reportCards = [
    {
      id: 'operations',
      title: 'Operations Log',
      icon: faHistory,
      description: 'Detailed chronological log of all crane operations including hoisting, lowering, and travel movements.',
      lastRun: '2 hours ago',
      size: '1.4MB'
    },
    {
      id: 'load',
      title: 'Load Analysis',
      icon: faWeightHanging,
      description: 'Comprehensive report on load weights, distribution, and overload incidents with visual charts.',
      lastRun: '1 day ago',
      size: '2.1MB'
    },
    {
      id: 'errors',
      title: 'Error Summary',
      icon: faExclamationTriangle,
      description: 'Aggregated error events with frequency analysis and resolution tracking.',
      lastRun: '3 hours ago',
      size: '0.8MB'
    },
    {
      id: 'performance',
      title: 'Performance Trends',
      icon: faChartLine,
      description: 'Visualization of crane performance metrics over time with comparative analysis.',
      lastRun: '1 week ago',
      size: '3.5MB'
    },
    {
      id: 'voltage',
      title: 'Voltage Analysis',
      icon: faBolt,
      description: 'Detailed report on voltage fluctuations and electrical system performance.',
      lastRun: '2 days ago',
      size: '1.7MB'
    },
    {
      id: 'temperature',
      title: 'Temperature Report',
      icon: faTemperatureHigh,
      description: 'Motor temperature trends and overheating incidents with recommendations.',
      lastRun: '4 hours ago',
      size: '1.2MB'
    }
  ];

  const quickActions = [
    { icon: faPlus, label: 'Create New Report' },
    { icon: faClock, label: 'Schedule Report' },
    { icon: faStar, label: 'Save as Template' },
    { icon: faShareAlt, label: 'Share Reports' },
    { icon: faCog, label: 'Report Settings' },
    { icon: faQuestionCircle, label: 'Help Guide' }
  ];

  const getStatusClass = (status) => {
    switch(status) {
      case 'ready': return 'status-ready';
      case 'processing': return 'status-processing';
      case 'failed': return 'status-failed';
      default: return '';
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'ready': return 'Ready';
      case 'processing': return 'Processing';
      case 'failed': return 'Failed';
      default: return status;
    }
  };

  const getCategoryBadgeClass = (category) => {
    switch(category) {
      case 'Standard': return 'badge-primary';
      case 'Custom': return 'badge-success';
      case 'Scheduled': return 'badge-warning';
      default: return '';
    }
  };

  const handleGenerateReport = () => {
    setIsGenerating(true);
    
    // Simulate report generation
    setTimeout(() => {
      setIsGenerating(false);
      showNotification('Report generated successfully!', 'success');
      
      // Add new report to the list
      const newReport = {
        id: `RPT-${new Date().toISOString().slice(0,10).replace(/-/g,'')}-${Math.floor(Math.random() * 1000).toString().padStart(3,'0')}`,
        type: reportCards.find(card => card.id === filters.reportType)?.title || 'Operations Log',
        category: 'Custom',
        dateRange: getDateRangeText(filters.timeRange),
        cranes: filters.craneSelection === 'all' ? 'All Cranes' : filters.craneSelection,
        generatedOn: new Date().toLocaleString(),
        status: 'ready'
      };
      
      setReports([newReport, ...reports]);
    }, 2000);
  };

  const getDateRangeText = (range) => {
    const today = new Date();
    switch(range) {
      case 'today':
        return today.toLocaleDateString();
      case 'week':
        const weekAgo = new Date(today.setDate(today.getDate() - 7));
        return `${weekAgo.toLocaleDateString()} - ${new Date().toLocaleDateString()}`;
      case 'month':
        const monthAgo = new Date(today.setMonth(today.getMonth() - 1));
        return `${monthAgo.toLocaleDateString()} - ${new Date().toLocaleDateString()}`;
      case 'quarter':
        const quarterAgo = new Date(today.setMonth(today.getMonth() - 3));
        return `${quarterAgo.toLocaleDateString()} - ${new Date().toLocaleDateString()}`;
      case 'year':
        const yearAgo = new Date(today.setFullYear(today.getFullYear() - 1));
        return `${yearAgo.toLocaleDateString()} - ${new Date().toLocaleDateString()}`;
      default:
        return 'Custom Range';
    }
  };

  const handleResetFilters = () => {
    setFilters({
      reportType: 'operations',
      timeRange: 'today',
      craneSelection: 'all',
      reportFormat: 'pdf'
    });
    showNotification('Filters reset', 'success');
  };

  const handleExport = (reportId, format) => {
    showNotification(`Exporting report ${reportId} as ${format.toUpperCase()}...`, 'success');
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      localStorage.removeItem('isLoggedIn');
      showNotification('Logged out successfully!', 'success');
      setTimeout(() => navigate('/login'), 1500);
    }
  };

  const handleReportCardAction = (cardId, action) => {
    if (action === 'generate') {
      setFilters(prev => ({ ...prev, reportType: cardId }));
      handleGenerateReport();
    } else if (action === 'preview') {
      showNotification(`Previewing ${cardId} report...`, 'success');
    }
  };

  return (
    <div className="reports-export-container">
      <div className="dashboard-content">
        {/* Header */}
        <div className="header">
          <div className="header-left">
            <div className="header-logo">CraneIQ</div>
          </div>

          <div className="user-info-wrapper">
                      <div className="account-dropdown">
                        <div className="account-trigger" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                          {orgLogo ? (
                                            <img
                                              src={orgLogo}
                                              alt="Organization Logo"
                                              style={{
                                                width: '32px',
                                                height: '32px',
                                                borderRadius: '50%',
                                                objectFit: 'cover',
                                                border: '2px solid #4361ee'
                                              }}
                                              onError={() => setOrgLogo(null)}
                                            />
                                          ) : (
                                            <FontAwesomeIcon icon={faUserCircle} style={{ fontSize: '1.5rem', color: '#4361ee' }} />
                                          )}
                          <span>{sessionStorage.getItem('username') || 'User'}</span>
                          <FontAwesomeIcon icon={faCaretDown} />
                        </div>
                        {isDropdownOpen && (
                          <div className="account-menu show">
                            <div className="account-menu-item plan-info">
                              <FontAwesomeIcon icon={faCrown} style={{ color: 'gold' }} />
                              <span>Basic Plan</span>
                            </div>
                            <div className="account-menu-divider"></div>
                            <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                              <FontAwesomeIcon icon={faUser} /> Profile
                            </button>
                            <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                              <FontAwesomeIcon icon={faSyncAlt} /> Upgrade Plan
                            </button>
                            <div className="account-menu-divider"></div>
                            <button className="account-menu-item logout-account-btn" onClick={handleLogout}>
                              <FontAwesomeIcon icon={faSignOutAlt} /> Logout
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
        </div>

        {/* Page Title */}
        <div className="page-title">
          <h1>Reports & Data Export</h1>
          <p>Generate, analyze, and export crane operation data</p>
        </div>

        {/* Report Dashboard */}
        <div className="report-dashboard">
          <div>
            {/* Report Stats */}
            <div className="report-stats">
              <div className="stat-card">
                <div className="icon primary">
                  <FontAwesomeIcon icon={faFileAlt} />
                </div>
                <div className="stat-valuec">{stats.reportsGenerated}</div>
                <div className="stat-label">Reports Generated</div>
                <div className="stat-label">This Month</div>
              </div>
              <div className="stat-card">
                <div className="icon success">
                  <FontAwesomeIcon icon={faChartLine} />
                </div>
                <div className="stat-valuec">{stats.avgReportSize}</div>
                <div className="stat-label">Avg. Report Size</div>
                <div className="stat-label">PDF format</div>
              </div>
              <div className="stat-card">
                <div className="icon warning">
                  <FontAwesomeIcon icon={faClock} />
                </div>
                <div className="stat-valuec">{stats.scheduledReports}</div>
                <div className="stat-label">Scheduled Reports</div>
                <div className="stat-label">Active</div>
              </div>
              <div className="stat-card">
                <div className="icon danger">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                </div>
                <div className="stat-valuec">{stats.alertReports}</div>
                <div className="stat-label">Alert Reports</div>
                <div className="stat-label">This Week</div>
              </div>
            </div>

            {/* Report Types */}
            <div className="report-types">
              {reportCards.map((card) => (
                <div className="report-card" key={card.id}>
                  <h3>
                    <FontAwesomeIcon icon={card.icon} />
                    {card.title}
                  </h3>
                  <p>{card.description}</p>
                  <div className="report-meta">
                    <span>Last run: {card.lastRun}</span>
                    <span>Size: {card.size}</span>
                  </div>
                  <div className="report-actions">
                    <button 
                      className="btn btn-primary"
                      onClick={() => handleReportCardAction(card.id, 'generate')}
                    >
                      Generate
                    </button>
                    <button 
                      className="btn btn-secondary"
                      onClick={() => handleReportCardAction(card.id, 'preview')}
                    >
                      Preview
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions Panel */}
          <div className="quick-actions">
            <h3>Quick Actions</h3>
            {quickActions.map((action, index) => (
              <button 
                className="action-btnsss" 
              >
                <FontAwesomeIcon icon={action.icon} />
                {action.label}
              </button>
            ))}
          </div>
        </div>

        {/* Report Filters */}
        <div className="report-filters">
          <div className="filter-group">
            <label className="filter-label">Report Type</label>
            <select 
              className="filter-select"
              value={filters.reportType}
              onChange={(e) => setFilters(prev => ({ ...prev, reportType: e.target.value }))}
            >
              <option value="operations">Operations Log</option>
              <option value="load">Load Analysis</option>
              <option value="errors">Error Summary</option>
              <option value="performance">Performance Trends</option>
              <option value="voltage">Voltage Analysis</option>
              <option value="temperature">Temperature Report</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Time Range</label>
            <select 
              className="filter-select"
              value={filters.timeRange}
              onChange={(e) => setFilters(prev => ({ ...prev, timeRange: e.target.value }))}
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Crane Selection</label>
            <select 
              className="filter-select"
              value={filters.craneSelection}
              onChange={(e) => setFilters(prev => ({ ...prev, craneSelection: e.target.value }))}
            >
              <option value="all">All Cranes</option>
              <option value="CRN-001">Gantry Crane #1 (CRN-001)</option>
              <option value="CRN-002">Overhead Crane #2 (CRN-002)</option>
              <option value="CRN-003">Jib Crane #3 (CRN-003)</option>
              <option value="CRN-004">Bridge Crane #4 (CRN-004)</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Report Format</label>
            <select 
              className="filter-select"
              value={filters.reportFormat}
              onChange={(e) => setFilters(prev => ({ ...prev, reportFormat: e.target.value }))}
            >
              <option value="pdf">PDF (Portable Document)</option>
              <option value="excel">Excel (XLSX)</option>
              <option value="csv">CSV (Comma Separated)</option>
              <option value="json">JSON</option>
            </select>
          </div>

          <div className="action-buttons">
            <button 
              className="btn btn-primary" 
              onClick={handleGenerateReport}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Generating...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faFileAlt} /> Generate Report
                </>
              )}
            </button>
            <button className="btn btn-secondary">
              <FontAwesomeIcon icon={faEye} /> Preview
            </button>
            <button className="btn btn-secondary" onClick={handleResetFilters}>
              <FontAwesomeIcon icon={faRedoAlt} /> Reset
            </button>
          </div>
        </div>

        {/* Operations Chart */}
        <div className="chart-containerss">
          <div className="chart-header">
            <h3>Operations Trend</h3>
          </div>
          <div className="chart-wrapperss">
            <Bar ref={operationsChartRef} data={chartData} options={chartOptions} />
          </div>
        </div>

        {/* Generated Reports Table */}
        <div className="report-table-container">
          <div className="table-header">
            <h3>Recent Reports</h3>
            <div className="table-actions">
              <button className="btn btn-secondary">
                <FontAwesomeIcon icon={faSyncAlt} /> Refresh
              </button>
              <button className="btn btn-secondary">
                <FontAwesomeIcon icon={faFilter} /> Filter
              </button>
            </div>
          </div>
          <table className="report-table">
            <thead>
              <tr>
                <th>Report ID</th>
                <th>Type</th>
                <th>Date Range</th>
                <th>Cranes</th>
                <th>Generated On</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => (
                <tr key={report.id}>
                  <td>{report.id}</td>
                  <td>
                    <div>{report.type}</div>
                    <div>
                      <span className={`badge ${getCategoryBadgeClass(report.category)}`}>
                        {report.category}
                      </span>
                    </div>
                  </td>
                  <td>{report.dateRange}</td>
                  <td>{report.cranes}</td>
                  <td>{report.generatedOn}</td>
                  <td>
                    <span className={`report-status ${getStatusClass(report.status)}`}>
                      {getStatusText(report.status)}
                    </span>
                  </td>
                  <td>
                    {report.status === 'ready' && (
                      <>
                        <button 
                          className="export-btn pdf" 
                          title="Download PDF"
                          onClick={() => handleExport(report.id, 'pdf')}
                        >
                          <FontAwesomeIcon icon={faFilePdf} />
                        </button>
                        <button 
                          className="export-btn excel" 
                          title="Download Excel"
                          onClick={() => handleExport(report.id, 'excel')}
                        >
                          <FontAwesomeIcon icon={faFileExcel} />
                        </button>
                      </>
                    )}
                    {report.status === 'processing' && (
                      <button className="export-btn" disabled title="Preparing">
                        <FontAwesomeIcon icon={faSpinner} spin />
                      </button>
                    )}
                    {report.status === 'failed' && (
                      <>
                        <button className="btn btn-secondary" title="Retry">
                          <FontAwesomeIcon icon={faRedoAlt} />
                        </button>
                        <button className="btn btn-secondary" title="View Error">
                          <FontAwesomeIcon icon={faInfoCircle} />
                        </button>
                      </>
                    )}
                    <button className="btn btn-secondary" title="Share">
                      <FontAwesomeIcon icon={faShareAlt} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="pagination">
            <li className="page-item">
              <button className="page-link">
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
            </li>
            <li className="page-item">
              <button className="page-link active">1</button>
            </li>
            <li className="page-item">
              <button className="page-link">2</button>
            </li>
            <li className="page-item">
              <button className="page-link">3</button>
            </li>
            <li className="page-item">
              <button className="page-link">
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </li>
          </div>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`notification notification-${notification.type} show`}>
          <FontAwesomeIcon icon={notification.type === 'success' ? faCheckCircle : faExclamationCircle} />
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default Reports;